import { useEffect, useRef, useState } from 'react';
import OBR from '@owlbear-rodeo/sdk';
import { createObrStorage, measureStoreBytes, DEFAULT_MAX_BYTES } from './obr/obrStorage.ts';
import { CharacterService, type SheetView } from './services/characterService.ts';
import { VisibilityService } from './services/visibilityService.ts';
import { createCharacter } from '@dcc/rules/app/characterFactory.ts';
import type { Character } from '@dcc/rules/domain/character.ts';
import { CharacterSheet } from './components/CharacterSheet.tsx';
import type { SheetActionHandlers } from './components/SheetActions.tsx';
import { applyDamage, applyHeal, applySpendMana, applyRestoreMana } from './services/actionService.ts';
import { exportCrawler, importCrawler } from './services/portabilityService.ts';
import { applyTablePlayEdits } from './services/editService.ts';
import { PortabilityPanel } from './components/PortabilityPanel.tsx';
import { SaveStatus } from './components/SaveStatus.tsx';
import type { LocalMirrorPlayer } from './obr/localMirror.ts';
import { GmBridgePanel } from './components/GmBridgePanel.tsx';
import { bridgeRequest } from './obr/gmBridgeClient.ts';
import { readBridgeKey } from './obr/gmBridgeConfig.ts';
import { buildGmRoster, type GmRosterCharacter, type GmRosterPlayer } from './obr/gmSheetRoster.ts';
import { PlayerCodePanel } from './components/PlayerCodePanel.tsx';
import { clearPlayerCode } from './obr/playerCode.ts';
import { clearSheetsPresence, publishSheetsPresence, readSheetsPresence } from './obr/sheetsPresence.ts';
import { createSheetsApiStorage, fetchPoster, SheetsSaveError, type RoomInventory } from './obr/sheetsApiStorage.ts';
import { PlayPartyTab } from './play/PlayPartyTab.tsx';
import { usableRoomItems, usableRoomWeapons } from './play/roomInventory.ts';
import { runItemPower } from './play/itemPowerFlow.ts';
import { markCombatActions } from './play/combatActionSync.ts';
import { CreatorScreen } from './components/creator/CreatorScreen.tsx';
import { createServices } from '@dcc/rules/app/services.ts';
import { SheetMutationGate } from '@dcc/rules/app/sheetMutationGate.ts';
import { PlayAttackTab } from './play/PlayAttackTab.tsx';
import { AchievementNotice, PlayAchievementsTab } from './play/PlayAchievementsTab.tsx';
import { PlayScratchpadTab } from './play/PlayScratchpadTab.tsx';
import { StablePlaySurface } from './components/StablePlaySurface.tsx';
import { PlaySheetTab } from './play/PlaySheetTab.tsx';
import { PlayItemsTab } from './play/PlayItemsTab.tsx';
import { PlayCompanionTab } from './play/PlayCompanionTab.tsx';
import { loadAllCustomContent } from '@dcc/rules/app/customContentBootstrap.ts';
import { syncPartyTimeEvents } from './play/partyTimeSync.ts';
import { dicePlusRequester } from './play/dicePlusRequester.ts';
import { isSheetSyncSignal, SHEET_SYNC_CHANNEL } from './obr/sheetSync.ts';
import { LibraryView } from './library/LibraryView.tsx';
import {
  LIBRARY_DOC, archiveCrawler, canDelete, emptyLibraryState, forgetCrawler, partitionLibrary, pruneLibraryState,
  readLibraryState, restoreCrawler, setActiveCrawler, type CrawlerLibraryState, type LibraryEntryView,
} from './library/crawlerLibrary.ts';
import { deleteCrawler } from '@dcc/rules/app/crawlerDeletionService.ts';
import type { RollMode } from '@dcc/rules/rules/rollModes.ts';

/**
 * Role decides the view:
 *  - PLAYER: connects their player code first, then their own crawler sheet,
 *            saved on the sheet server (sheetsApiStorage).
 *  - GM:     the GM's own crawlers (with a player-visibility toggle each),
 *            plus a read-only roster of every player's visible sheet.
 *
 * `player` is OBR.player wrapped in the browser-storage mirror (see
 * obr/localMirror.ts). Every read and write of this player's store goes
 * through it, so nothing here touches OBR.player directly.
 */
export function App({ role, player }: { role: 'GM' | 'PLAYER'; player: LocalMirrorPlayer }) {
  return role === 'GM' ? <GmView player={player} /> : <PlayerSheetView roomId={OBR.room.id} />;
}

/**
 * Build the interactive action handlers for one crawler. Each loads the current
 * character, runs the action through the rules engine (actionService), saves the
 * result, and refreshes the view. Returns a short note for the UI when the rules
 * engine has something to report (ignored damage, rejected overspend).
 */
function makeHandlers(
  service: CharacterService,
  id: string,
  refresh: () => Promise<void>,
): SheetActionHandlers {
  async function load(): Promise<Character | null> {
    const view = await service.loadSheet(id);
    return view ? view.character : null;
  }
  return {
    onDamage: async (amount) => {
      const c = await load(); if (!c) return;
      const out = applyDamage(c, amount);
      await service.save(out.character); await refresh();
      if (out.slotsLost === 0 && amount > 0) return `No slot lost — ${out.damageIgnored} damage was under one slot (ignored).`;
      return `${out.slotsLost} slot(s) lost${out.reducedToZero ? ' — down!' : ''}.`;
    },
    onHeal: async (slots) => {
      const c = await load(); if (!c) return;
      const out = applyHeal(c, slots);
      await service.save(out.character); await refresh();
      return `Healed ${out.slotsHealed} slot(s).`;
    },
    onSpendMana: async (cost) => {
      const c = await load(); if (!c) return;
      const out = applySpendMana(c, cost);
      if (!out.ok) return out.reason;
      await service.save(out.character); await refresh();
      return `Spent ${cost} mana.`;
    },
    onRestoreMana: async (amount) => {
      const c = await load(); if (!c) return;
      const updated = applyRestoreMana(c, amount);
      await service.save(updated); await refresh();
      return `Restored mana.`;
    },
    onRename: async (name) => {
      const c = await load(); if (!c) return;
      await service.save({ ...c, name }); await refresh();
    },
    onEdit: async (edits) => {
      const c = await load(); if (!c) return;
      const r = applyTablePlayEdits(c, edits);
      if (!r.ok) return r.problems[0] ?? 'Edit rejected by the rules.';
      await service.save(r.character); await refresh();
    },
  };
}

/** Import/export handlers for a storage service. Export serializes the given
 * crawler id; import parses text and saves it as a crawler in this service. */
function makePortability(
  service: CharacterService,
  refresh: () => Promise<void>,
  exportId: string | null,
) {
  return {
    onExport: exportId
      ? async () => {
          const view = await service.loadSheet(exportId);
          if (!view) return { error: 'No crawler to export.' };
          const r = exportCrawler(view.character);
          return r.ok ? { text: r.text, fileName: r.fileName } : { error: r.message };
        }
      : undefined,
    onImport: async (text: string) => {
      const r = importCrawler(text);
      if (!r.ok) return r.errors[0] ?? 'Could not read that crawler text.';
      // A new ID for every import, so importing never overwrites an existing crawler.
      await service.save({ ...r.character, id: newCrawlerId() });
      await refresh();
    },
  };
}

function newCrawlerId() {
  return `crawler-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function newCrawler() {
  return createCharacter({
    id: newCrawlerId(), name: 'New Crawler', crawlerType: 'human',
    stats: { str: 3, int: 3, con: 3, dex: 3, cha: 3 },
    skills: [], now: new Date().toISOString(),
  });
}

function StorageGauge({ bytes }: { bytes: number }) {
  const pct = Math.round((bytes / DEFAULT_MAX_BYTES) * 100);
  return (
    <footer className="dcc-budget" aria-label="sheet storage used">
      Sheet storage: {bytes} / {DEFAULT_MAX_BYTES} bytes ({pct}%)
    </footer>
  );
}

/**
 * Player view. Code first: nothing about a crawler shows until the player has
 * connected the code their GM sent. After that the sheet lives on the server.
 */
function PlayerSheetView({ roomId }: { roomId: string }) {
  const [session, setSession] = useState<{ code: string; label: string; playerId: string } | null>(null);
  const [panelKey, setPanelKey] = useState(0);
  const [panelMessage, setPanelMessage] = useState('');

  function authLost(message: string) {
    clearPlayerCode(roomId);
    setSession(null);
    setPanelMessage(message);
    setPanelKey((k) => k + 1);
  }

  useEffect(() => {
    void (session
      ? publishSheetsPresence(session.playerId, session.label)
      : clearSheetsPresence()
    ).catch((error) => console.warn('DCC sheets presence metadata could not be updated', error));
  }, [session?.playerId, session?.label, roomId]);

  return (
    <div className="dcc-panel">
      <PlayerCodePanel
        key={panelKey}
        roomId={roomId}
        initialMessage={panelMessage}
        onConnected={(code, label, playerId) => setSession((s) => (s && s.code === code && s.playerId === playerId ? s : { code, label, playerId }))}
        onDisconnected={() => setSession(null)}
      />
      {session
        ? <ConnectedSheet key={session.code} code={session.code} roomId={roomId} onAuthLost={authLost} />
        : <p className="dcc-empty">Connect your player code to create or open your crawler.</p>}
    </div>
  );
}

/** Play-surface tabs. Each tab reads the saved crawler when it opens, so they never hold stale copies. */
type SheetTab = 'sheet' | 'items' | 'attack' | 'companion' | 'party' | 'scratchpad' | 'achievements';

function SheetTabs({ tab, onChange }: { tab: SheetTab; onChange: (tab: SheetTab) => void }) {
  const tabs: [SheetTab, string][] = [['sheet', 'Sheet'], ['items', 'Inventory'], ['attack', 'Combat'], ['companion', 'Companion'], ['party', 'Party'], ['scratchpad', 'Scratchpad'], ['achievements', 'Achievements']];
  return (
    <nav className="dcc-tabs">
      {tabs.map(([id, label]) => (
        <button key={id} className={tab === id ? 'dcc-tab is-active' : 'dcc-tab'} onClick={() => onChange(id)}>{label}</button>
      ))}
    </nav>
  );
}

function ConnectedSheet({ code, roomId, onAuthLost }: { code: string; roomId: string; onAuthLost: (message: string) => void }) {
  const [storage] = useState(() => createSheetsApiStorage(fetchPoster(code, roomId)));
  const [service] = useState(() => new CharacterService(storage));
  const [engine] = useState(() => createServices(storage));
  const [tab, setTab] = useState<SheetTab>('sheet');
  const [notesDirty, setNotesDirty] = useState(false);
  function changeTab(next: SheetTab) {
    if (next !== tab && notesDirty && !window.confirm('Discard unsaved scratchpad changes? Save notes first if you want to keep them.')) return;
    if (next !== 'attack') { setCombatSkillId(null); setCombatMode(null); }
    setTab(next);
  }
  const [combatSkillId, setCombatSkillId] = useState<string | null>(null);
  const [combatMode, setCombatMode] = useState<RollMode | null>(null);
  const [screen, setScreen] = useState<'library' | 'sheet' | 'creator'>('library');
  const [openId, setOpenId] = useState<string | null>(null);
  const [view, setView] = useState<SheetView | null>(null);
  const [entries, setEntries] = useState<LibraryEntryView[]>([]);
  const [libraryState, setLibraryState] = useState<CrawlerLibraryState>(emptyLibraryState());
  const [phase, setPhase] = useState<'loading' | 'ready' | 'failed'>('loading');
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<{ text: string; warn: boolean }>({ text: '', warn: false });
  const [playEpoch, setPlayEpoch] = useState(0);
  const [roomInventory, setRoomInventory] = useState<RoomInventory | null>(null);
  const roomCatalogueReady = useRef(false);
  const partySyncing = useRef(false);
  const coordinationGate = useRef<SheetMutationGate | null>(null);
  if (coordinationGate.current === null) coordinationGate.current = new SheetMutationGate();

  /** Re-read the library and the open crawler from the snapshot already loaded (no network). */
  async function refresh(open: string | null = openId): Promise<{ state: CrawlerLibraryState; ids: string[] }> {
    const ids = await service.listIds();
    const views = (await Promise.all(ids.map((id) => service.loadSheet(id)))).filter((v): v is SheetView => v !== null);
    setEntries(views.map((v) => ({ id: v.character.id, name: v.character.name, level: v.character.level, floor: v.character.floor, crawlerType: v.character.crawlerType })));
    const state = pruneLibraryState(readLibraryState(await storage.readCustomContent(LIBRARY_DOC)), ids);
    setLibraryState(state);
    const current = open && ids.includes(open) ? views.find((v) => v.character.id === open) ?? null : null;
    setView(current);
    if (open && !current) { setOpenId(null); setScreen('library'); }
    return { state, ids };
  }

  /** Fetch the latest copy from the server, then re-render. */
  async function reload() {
    await storage.load();
    await refreshRoomInventory(true);
    await refresh();
  }

  async function recover(error: unknown): Promise<string> {
    if (error instanceof SheetsSaveError && error.reason === 'auth') {
      onAuthLost(error.message);
      return error.message;
    }
    if (error instanceof SheetsSaveError && error.reason === 'stale') {
      const note = `${error.message} It has been reloaded; please try again.`;
      try { await reload(); setPlayEpoch(value => value + 1); setStatus({ text: note, warn: true }); }
      catch (reloadError) { return recover(reloadError); }
      return note;
    }
    const text = error instanceof Error ? error.message : 'Your last change was not saved.';
    setStatus({ text: `Not saved: ${text}`, warn: true });
    return `Not saved: ${text}`;
  }

  const saved = () => setStatus({ text: 'Saved to your account.', warn: false });

  /**
   * Composes the live catalogue from this player's own content PLUS the Items
   * that belong to the room (loot, GM awards, the Party Inventory), so an Item
   * a crawler holds means the same thing on every sheet at the table.
   */
  async function composeCatalogue(room: RoomInventory | null) {
    const shared = usableRoomItems(room?.roomItems ?? []);
    // GM-made weapons bring their own Skill and Attack, or they could not be swung.
    const weapons = usableRoomWeapons(room?.roomSkills ?? [], room?.roomAttacks ?? []);
    const custom = await loadAllCustomContent(engine, { items: shared.items, skills: weapons.skills, attacks: weapons.attacks });
    return [...shared.problems, ...weapons.problems, ...custom.problems];
  }

  /** Fetches the room's shared items and recomposes the catalogue. Never throws: the sheet works without the stash. */
  async function refreshRoomInventory(strict = false): Promise<string[]> {
    let room: RoomInventory | null = null;
    let problems: string[] = [];
    try { room = await storage.getRoomInventory(); }
    catch (error) {
      if (strict || !roomCatalogueReady.current || (error instanceof SheetsSaveError && error.reason === 'auth')) throw error;
      // Keep the last working room catalogue on a transient failure. Removing
      // its equipment bonuses could otherwise change Health/Mana calculations.
      return ['The Party Inventory could not be loaded. Try Refresh again.'];
    }
    problems = [...problems, ...(await composeCatalogue(room))];
    roomCatalogueReady.current = true;
    setRoomInventory(room);
    return problems;
  }

  /** The server moved an item for this player: adopt its stash, re-read the crawler it changed, refresh the play records. */
  async function adoptMovedInventory(room: RoomInventory) {
    await storage.load();
    await composeCatalogue(room);
    setRoomInventory(room);
    await refresh(openId);
    setPlayEpoch((value) => value + 1);
  }

  async function pollPartyTime(forceRebase = false) {
    if (busy || partySyncing.current) return;
    partySyncing.current = true;
    try {
      const gate = coordinationGate.current;
      if (gate === null) return;
      const result = await syncPartyTimeEvents(storage, engine, gate, dicePlusRequester, forceRebase, async () => {
        const problems = await refreshRoomInventory(true);
        if (problems.length > 0) throw new Error(problems.join(' '));
      });
      // Items moved somewhere in the room (the revision rides on the poll just
      // made). Learn the new definitions BEFORE any tab re-reads a crawler that
      // may now be holding one of them.
      if (!result.deferred && storage.roomInventoryChanged() && gate.claim() === null) {
        try {
          const problems = await refreshRoomInventory();
          if (problems.length > 0) setStatus({ text: problems.join(' '), warn: true });
          setPlayEpoch((value) => value + 1);
        } finally { gate.release(); }
      }
      if (result.rebased) {
        await refresh(openId);
        // Every play tab owns an in-memory Character/Session/Runtime snapshot.
        // A party-event sync rebases the shared storage cache even when another
        // tab already applied the event, so refresh after ANY such rebase. This
        // prevents an old play snapshot from saving over newly refreshed state.
        setPlayEpoch((value) => value + 1);
      }
      if (result.applied > 0) {
        setStatus({ text: result.notes.join(' '), warn: false });
      }
      if (result.blocked.length > 0) {
        setStatus({ text: result.blocked.join(' '), warn: true });
      }
    } catch (error) {
      await recover(error);
    } finally {
      partySyncing.current = false;
    }
  }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await storage.load();
        // The live rules catalogue includes this player's custom Skills/Items.
        // Load it before any crawler is opened so equipped custom Items and
        // custom weapon/Spell links resolve through the same registry as core.
        const catalogueProblems = await refreshRoomInventory();
        const { state, ids } = await refresh(null);
        if (cancelled) return;
        // Open straight into the active crawler when there is one.
        if (state.activeCharacterId && ids.includes(state.activeCharacterId)) {
          setOpenId(state.activeCharacterId);
          await refresh(state.activeCharacterId);
          setScreen('sheet');
        }
        setPhase('ready');
        if (catalogueProblems.length > 0) {
          setStatus({ text: catalogueProblems.join(' '), warn: true });
        } else {
          saved();
        }
      } catch (error) {
        if (cancelled) return;
        const text = await recover(error);
        setStatus({ text, warn: true });
        setPhase('failed');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Party time is a server event, not a player button. Poll lightly while the
  // connected sheet is open; pending events survive while the player is away.
  useEffect(() => {
    if (phase !== 'ready') return;
    void pollPartyTime();
    const timer = window.setInterval(() => { void pollPartyTime(); }, 5_000);
    return () => window.clearInterval(timer);
  }, [phase, openId, busy]);


  // The GM's Owlbear background receives a short-lived bridge command after a
  // successful GM-side save/event and broadcasts this nudge to everyone in the
  // room. It is only a latency improvement: D1 is authoritative and the 5 s
  // poll/reconnect path above remains the fallback if this message is missed.
  useEffect(() => {
    if (phase !== 'ready') return;
    return OBR.broadcast.onMessage(SHEET_SYNC_CHANNEL, (event: { data: unknown }) => {
      if (!isSheetSyncSignal(event.data)) return;
      void pollPartyTime(true);
    });
  }, [phase, openId, busy]);

  /** Runs one library change: save, re-read, report. */
  async function libraryAction(task: () => Promise<void>) {
    if (busy) return;
    setBusy(true);
    try { await task(); saved(); }
    catch (error) { await recover(error); }
    finally { setBusy(false); }
  }

  async function writeLibrary(next: CrawlerLibraryState) {
    await storage.writeCustomContent(LIBRARY_DOC, next);
    setLibraryState(next);
  }

  function openCrawler(id: string) {
    setOpenId(id);
    setTab('sheet');
    setScreen('sheet');
    void refresh(id);
  }

  function makeActive(id: string | null) {
    void libraryAction(async () => {
      const change = setActiveCrawler(libraryState, id);
      if (!change.ok) { setStatus({ text: change.reason, warn: true }); return; }
      await writeLibrary(change.state);
    });
  }

  function archive(id: string) {
    void libraryAction(async () => {
      await writeLibrary(archiveCrawler(libraryState, id));
      if (openId === id) { setOpenId(null); setScreen('library'); }
      await refresh(openId === id ? null : openId);
    });
  }

  function restore(id: string) {
    void libraryAction(async () => {
      await writeLibrary(restoreCrawler(libraryState, id));
      await refresh();
    });
  }

  function remove(id: string) {
    void libraryAction(async () => {
      const problem = canDelete(libraryState, id);
      if (problem) { setStatus({ text: problem, warn: true }); return; }
      await deleteCrawler(id, { characters: engine.repository, sessions: engine.sessions, runtimes: engine.runtime, storage });
      await writeLibrary(forgetCrawler(libraryState, id));
      await refresh(null);
    });
  }

  /** Saves a crawler built by the creator. Returns a message when it did not save. */
  async function createFromCreator(character: Character): Promise<string | void> {
    try {
      await service.save(character);
      // The first crawler a player makes becomes their active one.
      if (!libraryState.activeCharacterId) await writeLibrary({ ...libraryState, activeCharacterId: character.id });
      openCrawler(character.id);
      saved();
    } catch (error) {
      return recover(error);
    }
  }

  const statusLine = status.text && (
    <p className={status.warn ? 'dcc-save-status dcc-save-warn' : 'dcc-save-status'} role={status.warn ? 'alert' : 'status'}>
      {status.text}
    </p>
  );

  if (phase === 'loading') return <p>Loading your crawlers…</p>;
  if (phase === 'failed') {
    return (
      <div>
        <p className="dcc-save-status dcc-save-warn" role="alert">{status.text}</p>
        <button onClick={() => { setPhase('loading'); void reload().then(() => { setPhase('ready'); saved(); }, async (e) => { setStatus({ text: await recover(e), warn: true }); setPhase('failed'); }); }}>
          Try again
        </button>
      </div>
    );
  }

  if (screen === 'creator') {
    return <CreatorScreen newId={newCrawlerId} onCreate={createFromCreator} onCancel={() => setScreen('library')} />;
  }

  if (screen === 'library' || !view) {
    const parts = partitionLibrary(entries, libraryState);
    const portability = makePortability(service, () => refresh(openId).then(() => undefined), null);
    return (
      <>
        <LibraryView
          library={parts.library}
          archived={parts.archived}
          activeId={libraryState.activeCharacterId}
          busy={busy}
          onOpen={openCrawler}
          onSetActive={(id) => makeActive(id)}
          onArchive={archive}
          onRestore={restore}
          onDelete={remove}
          onCreate={() => setScreen('creator')}
        />
        <PortabilityPanel
          canExport={false}
          handlers={{
            onImport: async (text: string) => {
              try { const problem = await portability.onImport(text); if (!problem) saved(); return problem; }
              catch (error) { return recover(error); }
            },
          }}
        />
        {statusLine}
      </>
    );
  }

  const isActive = libraryState.activeCharacterId === view.character.id;
  const sheetBar = (
    <><AchievementNotice key={view.character.id} awards={roomInventory?.awards} characterId={view.character.id} onOpen={() => changeTab('achievements')} /><div className="dcc-sheet-bar">
      <button className="dcc-creator-secondary" onClick={() => { if (notesDirty && !window.confirm('Discard unsaved scratchpad changes?')) return; setScreen('library'); setOpenId(null); void refresh(null); }}>← Library</button>
      <button
        className={isActive ? 'dcc-active-toggle is-on' : 'dcc-active-toggle'}
        disabled={busy}
        aria-pressed={isActive}
        onClick={() => makeActive(isActive ? null : view.character.id)}
      >
        {isActive ? '★ Active crawler' : '☆ Set as active crawler'}
      </button>
    </div></>
  );

  if (tab !== 'sheet') {
    const playTab = tab === 'items'
      ? <PlayItemsTab
          key={`items-${view.character.id}`} refreshToken={playEpoch}
          services={engine}
          characterId={view.character.id}
          onSaveError={recover}
          coordinationGate={coordinationGate.current}
          combatStorage={storage}
          onOpenCombat={(skillId, mode) => { setCombatSkillId(skillId); setCombatMode(mode ?? null); setTab('attack'); }}
          itemPowers={{
            states: roomInventory?.powerStates ?? {},
            use: async (itemId) => {
              const result = await runItemPower(storage, engine, view.character.id, itemId);
              // Mark the Action on the GM's shared tracker, as an attack or a Spell does. Never blocks the use itself.
              if (result.ok && result.combatActions > 0) {
                try {
                  const tracked = await markCombatActions(storage, view.character.id, 1, 'player-item');
                  if (tracked.outOfActions) await OBR.notification.show('Out Of Actions', 'WARNING');
                } catch (error) {
                  await OBR.notification.show(error instanceof Error ? error.message : 'Combat tracker update failed.', 'WARNING');
                }
              }
              return result;
            },
            // A used-up Item may have left the catalogue's reach; recompose before showing it.
            onUsed: async (room) => { await composeCatalogue(room); setRoomInventory(room); },
          }}
        />
      : tab === 'achievements'
        ? <PlayAchievementsTab awards={roomInventory?.awards} characterId={view.character.id} />
      : tab === 'scratchpad'
        ? <PlayScratchpadTab key={`notes-${view.character.id}`} services={engine} characterId={view.character.id} coordinationGate={coordinationGate.current} onSaveError={recover} onDirty={setNotesDirty} onRefresh={() => storage.load()} />
      : tab === 'party'
        ? <PlayPartyTab
            key="party"
            services={engine}
            storage={storage}
            characterId={view.character.id}
            inventory={roomInventory}
            epoch={playEpoch}
            onSaveError={recover}
            coordinationGate={coordinationGate.current}
            onMoved={adoptMovedInventory}
            onRefresh={async () => {
              const gate = coordinationGate.current!;
              if (gate.claim() !== null) return;
              try {
                const problems = await refreshRoomInventory();
                if (problems.length > 0) setStatus({ text: problems.join(' '), warn: true });
              } finally { gate.release(); }
            }}
          />
      : tab === 'companion'
        ? <PlayCompanionTab
            key={`companion-${view.character.id}`} refreshToken={playEpoch}
            services={engine}
            characterId={view.character.id}
            onSaveError={recover}
            coordinationGate={coordinationGate.current}
            combatStorage={storage}
          />
        : <PlayAttackTab
            key={`attack-${view.character.id}-${combatSkillId ?? 'none'}-${combatMode ?? 'normal'}`} refreshToken={playEpoch}
            services={engine}
            characterId={view.character.id}
            onSaveError={recover}
            coordinationGate={coordinationGate.current}
            combatStorage={storage}
            initialSkillId={combatSkillId}
            initialMode={combatMode}
          />;
    return (
      <>
        {sheetBar}
        <SheetTabs tab={tab} onChange={changeTab} />
        <StablePlaySurface key={`${view.character.id}-${tab}`}>{playTab}</StablePlaySurface>
      </>
    );
  }

  const portability = makePortability(service, () => refresh(openId).then(() => undefined), view.character.id);
  return (
    <>
      {sheetBar}
      <SheetTabs tab={tab} onChange={changeTab} />
      <StablePlaySurface><PlaySheetTab key={`sheet-${view.character.id}`} refreshToken={playEpoch} services={engine} characterId={view.character.id} onSaveError={recover} coordinationGate={coordinationGate.current} combatStorage={storage} /></StablePlaySurface>
      <PortabilityPanel
        canExport
        handlers={{
          onExport: portability.onExport,
          onImport: async (text: string) => {
            try { const problem = await portability.onImport(text); if (!problem) saved(); return problem; }
            catch (error) { return recover(error); }
          },
        }}
      />
      {statusLine}
    </>
  );
}

interface GmCrawler { id: string; view: SheetView; visible: boolean; }

function GmRosterCharacterDetails({ character, playerLabel, online }: { character: GmRosterCharacter; playerLabel: string; online: boolean }) {
  return (
    <details className="dcc-gm-tree-character">
      <summary>
        <strong>{character.name}</strong>
        <span>{playerLabel} · Level {character.view.character.level}{character.active ? ' · Active' : ''}{character.archived ? ' · Archived' : ''}{online ? ' · Online' : ''}</span>
      </summary>
      <CharacterSheet view={character.view} />
    </details>
  );
}

/**
 * GM sheet browser. The GM's bridge secret proves GM access to Cloudflare;
 * Cloudflare uses the sheets GM secret internally, so that secret never needs
 * to live in the Owlbear browser. The browser gets a read-only full-sheet
 * roster plus the GM's older locally-owned crawler tools.
 */
function GmView({ player }: { player: LocalMirrorPlayer }) {
  const [charSvc] = useState(() => new CharacterService(createObrStorage(player)));
  const [visSvc] = useState(() => new VisibilityService(player));
  const [mine, setMine] = useState<GmCrawler[]>([]);
  const [roster, setRoster] = useState<GmRosterPlayer[]>([]);
  const [onlinePlayerIds, setOnlinePlayerIds] = useState<Set<string>>(() => new Set());
  const [rosterStatus, setRosterStatus] = useState('');
  const [bytes, setBytes] = useState(0);
  const [loading, setLoading] = useState(true);

  async function refresh() {
    const ids = await charSvc.listIds();
    const visMap = await visSvc.getMap();
    const rows: GmCrawler[] = [];
    for (const id of ids) {
      const view = await charSvc.loadSheet(id);
      if (view) rows.push({ id, view, visible: visMap[id] !== false });
    }
    setMine(rows);

    try {
      const connected = await OBR.party.getPlayers();
      const online = new Set<string>();
      for (const member of connected) {
        if (member.role !== 'PLAYER') continue;
        const presence = readSheetsPresence(member.metadata);
        if (presence) online.add(presence.playerId);
      }
      setOnlinePlayerIds(online);
    } catch {
      setOnlinePlayerIds(new Set());
    }

    const bridgeKey = readBridgeKey(OBR.room.id);
    if (!bridgeKey) {
      setRoster([]);
      setRosterStatus('Configure the Obsidian GM Bridge to load the server-backed player libraries.');
    } else {
      try {
        const result = await bridgeRequest(bridgeKey, OBR.room.id, { action: 'sheetsRoster' });
        if (result.ok) {
          setRoster(buildGmRoster(result.players, result.documents));
          setRosterStatus('');
        } else {
          setRoster([]);
          setRosterStatus(result.error ?? 'Could not load the server-backed player libraries.');
        }
      } catch {
        setRoster([]);
        setRosterStatus('Could not reach the DCC sheets bridge.');
      }
    }

    const meta = await player.getMetadata();
    setBytes(measureStoreBytes(meta['com.lordofdisndat.dcc/store'] ?? {}));
    setLoading(false);
  }

  useEffect(() => {
    void refresh();
    const unsub = OBR.party.onChange(() => { void refresh(); });
    return () => unsub();
  }, []);

  async function create() { setLoading(true); await charSvc.save(newCrawler()); await refresh(); }
  async function toggle(id: string) { await visSvc.toggle(id); await refresh(); }

  const activeCharacters = roster.flatMap((owner) => owner.revoked
    ? []
    : owner.characters.filter((character) => character.active).map((character) => ({ owner, character })));
  const activePlayers = roster.filter((owner) => !owner.revoked);

  if (loading) return <div className="dcc-panel"><p>Loading…</p></div>;
  return (
    <div className="dcc-panel">
      <div className="dcc-gm-header">
        <h1>DCC Crawler Sheets</h1>
        <button onClick={() => void refresh()}>Refresh</button>
      </div>

      <GmBridgePanel />

      <details className="dcc-gm-tree dcc-gm-sheet-browser" open>
        <summary><strong>Character Sheets</strong></summary>
        {rosterStatus ? <p className="dcc-empty">{rosterStatus}</p> : null}

        <details className="dcc-gm-tree-section" open>
          <summary><strong>Active Characters</strong><span>{activeCharacters.length}</span></summary>
          {activeCharacters.length === 0
            ? <p className="dcc-empty">No player currently has an active crawler.</p>
            : activeCharacters.map(({ owner, character }) => (
                <GmRosterCharacterDetails
                  key={`active:${owner.playerId}:${character.id}`}
                  character={character}
                  playerLabel={owner.label}
                  online={onlinePlayerIds.has(owner.playerId)}
                />
              ))}
        </details>

        <details className="dcc-gm-tree-section">
          <summary><strong>Players</strong><span>{activePlayers.length}</span></summary>
          {roster.length === 0 && !rosterStatus
            ? <p className="dcc-empty">No players have been created for this room.</p>
            : roster.map((owner) => (
                <details key={owner.playerId} className="dcc-gm-tree-player">
                  <summary>
                    <strong>{owner.label}</strong>
                    <span>{owner.revoked ? 'Revoked' : onlinePlayerIds.has(owner.playerId) ? 'Online' : 'Offline'} · {owner.characters.length} crawler{owner.characters.length === 1 ? '' : 's'}</span>
                  </summary>
                  {owner.characters.length === 0
                    ? <p className="dcc-empty">No crawlers in this player's library.</p>
                    : owner.characters.map((character) => (
                        <GmRosterCharacterDetails
                          key={`${owner.playerId}:${character.id}`}
                          character={character}
                          playerLabel={owner.label}
                          online={onlinePlayerIds.has(owner.playerId)}
                        />
                      ))}
                </details>
              ))}
        </details>
      </details>

      <details className="dcc-gm-tree dcc-gm-local-crawlers">
        <summary><strong>GM-owned local crawlers</strong><span>{mine.length}</span></summary>
        <div className="dcc-gm-header dcc-gm-local-actions">
          <span className="dcc-empty">Legacy GM/NPC library stored in Owlbear metadata.</span>
          <button onClick={create}>Create a crawler</button>
        </div>
        <PortabilityPanel
          canExport={mine.length > 0}
          handlers={makePortability(charSvc, refresh, mine.length > 0 ? mine[0]!.id : null)}
        />
        {mine.length === 0
          ? <p className="dcc-empty">No GM-owned local crawlers.</p>
          : mine.map((c) => (
              <section key={c.id} className="dcc-gm-crawler">
                <label className="dcc-visibility-toggle">
                  <input type="checkbox" checked={c.visible} onChange={() => toggle(c.id)} />
                  {c.visible ? 'Visible to players' : 'Hidden from players'}
                </label>
                <CharacterSheet view={c.view} actions={makeHandlers(charSvc, c.id, refresh)} />
              </section>
            ))}
      </details>

      <SaveStatus mirror={player} />
      <StorageGauge bytes={bytes} />
    </div>
  );
}
